/**
 * Times (teams) of the company and the official cargo (job title) that puts a
 * person in each one. The team is never stored: it is derived from the cargo,
 * so keeping the cargo right is enough to keep the team right. Used by the
 * /employees grid, the employee MCP tools and the pulse-survey MCP tools (to
 * break survey results down by team).
 */

export const ORG_TEAMS = [
  { value: "growth", label: "Growth" },
  { value: "sales", label: "Sales" },
  { value: "finance_ops", label: "Finance e Ops" },
  { value: "principal", label: "Principal" },
  { value: "forward_deployed", label: "Forward Deployed" },
  { value: "people_experts", label: "People Experts" },
  { value: "product_design", label: "Product & Design" },
  { value: "engineering", label: "Engineering" },
  { value: "data_science", label: "Data science" },
  { value: "recruiting", label: "Recruiting" },
  { value: "c_level", label: "C-level" },
] as const;

export type OrgTeam = (typeof ORG_TEAMS)[number]["value"];

/** Official cargo → team. A cargo outside this list has no team. */
export const JOB_TITLE_TEAMS: readonly { jobTitle: string; team: OrgTeam }[] = [
  { jobTitle: "Growth", team: "growth" },
  { jobTitle: "GTM Engineer", team: "growth" },
  // How Sellers/Hunters are called from now on.
  { jobTitle: "HR Executive", team: "sales" },
  { jobTitle: "Head of Sales", team: "sales" },
  { jobTitle: "Sales Ops & Enablement", team: "sales" },
  { jobTitle: "Finance & Ops", team: "finance_ops" },
  { jobTitle: "Finance Engineer", team: "finance_ops" },
  { jobTitle: "Compliance & Legal", team: "finance_ops" },
  { jobTitle: "Facilities", team: "finance_ops" },
  { jobTitle: "Principal", team: "principal" },
  { jobTitle: "Forward Deployed", team: "forward_deployed" },
  { jobTitle: "People Expert", team: "people_experts" },
  { jobTitle: "People Experts", team: "people_experts" },
  { jobTitle: "Product Manager", team: "product_design" },
  { jobTitle: "Designer", team: "product_design" },
  { jobTitle: "Software Engineer", team: "engineering" },
  // Different role from Forward Deployed — sits in Engineering.
  { jobTitle: "Forward Deployed Engineer", team: "engineering" },
  { jobTitle: "Data Engineer", team: "engineering" },
  { jobTitle: "Data Scientist", team: "data_science" },
  { jobTitle: "Recruiter", team: "recruiting" },
  { jobTitle: "CEO", team: "c_level" },
  { jobTitle: "CHRO", team: "c_level" },
];

const normalize = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

const TEAM_BY_JOB_TITLE = new Map<string, OrgTeam>(
  JOB_TITLE_TEAMS.map((entry) => [normalize(entry.jobTitle), entry.team]),
);

/**
 * The team a cargo belongs to, or null when the cargo is empty or not in the
 * official list. Matching ignores case and extra spaces only — "software
 * engineer " is Engineering, "Engenheiro de Software" has no team.
 */
export function teamForJobTitle(jobTitle: string | null | undefined): OrgTeam | null {
  if (!jobTitle) return null;
  return TEAM_BY_JOB_TITLE.get(normalize(jobTitle)) ?? null;
}

export function getOrgTeamLabel(team: string | null | undefined): string | null {
  if (!team) return null;
  return ORG_TEAMS.find((t) => t.value === team)?.label ?? null;
}

/** Shortcut: the team label for a cargo, or null. */
export function teamLabelForJobTitle(jobTitle: string | null | undefined): string | null {
  return getOrgTeamLabel(teamForJobTitle(jobTitle));
}
